"use client";

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productInterest: '',
    quantity: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          formType: 'contact',
          formData: formData,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitMessage('Thank you for your inquiry! We will respond within 24 hours.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          productInterest: '',
          quantity: '',
          message: '',
        });
      } else {
        setSubmitMessage('Failed to send inquiry. Please try again or contact us directly at fastminerals9@gmail.com');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitMessage('Failed to send inquiry. Please try again or contact us directly at fastminerals9@gmail.com');
    } finally {
      setIsSubmitting(false);
      // Clear message after 5 seconds
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <section className="py-32 bg-background">
      <div className="container mx-auto px-6">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-serif italic font-bold tracking-tight mb-4 text-foreground">
              Send Us an Inquiry
            </h2>
            <p className="text-lg text-muted">
              Fill out the form below and we'll respond within 24 hours
            </p>
          </div>

          <form onSubmit={handleSubmit} className="bg-card rounded-3xl p-8 shadow-2xl space-y-6">
            {/* Name */}
            <div>
              <label htmlFor="name" className="block text-sm font-bold text-foreground mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="John Doe"
              />
            </div>

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-bold text-foreground mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="john@company.com"
              />
            </div>

            {/* Phone */}
            <div>
              <label htmlFor="phone" className="block text-sm font-bold text-foreground mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="+1 234 567 8900"
              />
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className="block text-sm font-bold text-foreground mb-2">
                Company Name
              </label>
              <input
                type="text"
                id="company"
                value={formData.company}
                onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="Your Company Ltd"
              />
            </div>

            {/* Product Interest */}
            <div>
              <label htmlFor="productInterest" className="block text-sm font-bold text-foreground mb-2">
                Product Interest *
              </label>
              <select
                id="productInterest"
                required
                value={formData.productInterest}
                onChange={(e) => setFormData({ ...formData, productInterest: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select product type</option>
                <option value="gold">Gold (Bars, Nuggets, Dust)</option>
                <option value="precious">Precious Minerals (Tanzanite, Emeralds, Rubies)</option>
                <option value="semi-precious">Semi-Precious Minerals</option>
                <option value="custom">Custom Order</option>
              </select>
            </div>

            {/* Quantity */}
            <div>
              <label htmlFor="quantity" className="block text-sm font-bold text-foreground mb-2">
                Quantity Needed *
              </label>
              <input
                type="text"
                id="quantity"
                required
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="e.g., 500g, 100 carats, 1kg"
              />
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-bold text-foreground mb-2">
                Message *
              </label>
              <textarea
                id="message"
                required
                rows={6}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full px-4 py-3 rounded-xl border border-border bg-input text-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="Tell us about your requirements, delivery timeline, or any questions you have..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                  Sending...
                </span>
              ) : (
                'Send Inquiry'
              )}
            </button>

            {/* Success Message */}
            {submitMessage && (
              <div className="bg-success/10 border border-success rounded-xl p-4 text-center">
                <p className="text-success font-medium">{submitMessage}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}